/**
 * One frame for every player view.
 *
 * A stage sized purely by width (`width: 100%` + `aspect-ratio`) is unbounded
 * vertically: the moment its content is taller than the 16:9 stimulus — an
 * enlarged 4:3 / square / portrait face recording — the stage grows past the
 * fold and pushes the transport bar and the timeline off screen. So every view
 * renders into a frame of the same shape and the same height budget, and fits
 * its media inside that frame (see `fitWidthPct`) rather than letting the media
 * dictate the frame. Swapping the face in and out then never resizes the player.
 */
export const STAGE_ASPECT = 16 / 9

/** Tallest the player may get, however wide its column is. */
const STAGE_MAX_H = 'min(64vh, 680px)'

/** Width cap keeping a frame of ratio `aspect` inside the height budget. */
export const stageMaxWidth = (aspect: number = STAGE_ASPECT) => `calc(${STAGE_MAX_H} * ${aspect})`

/** The frame itself: full column width, 16:9, centred once the cap bites. */
export const stageFrameSx = {
  width: '100%',
  maxWidth: stageMaxWidth(),
  mx: 'auto',
  aspectRatio: `${STAGE_ASPECT}`,
}

/**
 * Width — as a percentage of the frame — at which media of ratio `aspect` fits
 * inside a frame of ratio `frameAspect`: the media spans the frame's full width
 * when it is the wider of the two, otherwise its full height. That is
 * `min(W, H·aspect)` expressed relative to W, since `H = W / frameAspect`.
 */
export const fitWidthPct = (aspect: number, frameAspect: number) =>
  `${Math.min(100, (100 * aspect) / frameAspect).toFixed(3)}%`
